import { Link } from 'react-router-dom';
import { useMe } from '../hooks/useAuth.js';
import { usePageMeta } from '../lib/usePageMeta.js';

export function ListYourTrailerPage() {
  const { data } = useMe();
  const signedIn = Boolean(data?.user);

  usePageMeta({
    title: 'List your trailer — earn $200–$600/month on idle equipment',
    description:
      'List your utility, dump, enclosed, car-hauler, landscape, snowmobile, or motorcycle trailer on TowShare. Free to list. Owners set their own price. ID-verified renters. $1,500 damage guarantee. Stripe payouts in 2–3 days.',
    canonicalPath: '/list-your-trailer',
    keywords:
      'list your trailer Ottawa, list your trailer Kingston, rent out trailer, peer to peer trailer income, trailer side income, TowShare owner',
  });

  return (
    <div>
      <section className="bg-gradient-to-br from-navy via-navy-600 to-navy-700 text-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
            For trailer owners
          </p>
          <h1 className="text-4xl md:text-5xl font-bold max-w-3xl mx-auto mb-4">
            Earn money on a trailer that sits idle.
          </h1>
          <p className="text-lg text-navy-100 max-w-2xl mx-auto mb-8">
            Most utility trailers are used a few weekends a year. Rent yours out the rest of the
            time. Set your own price. Cancel any time.
          </p>
          <Link
            to={signedIn ? '/listings/new' : '/login?next=/listings/new'}
            className="btn-primary text-base px-8"
          >
            List your trailer
          </Link>
          <p className="text-sm text-navy-200 mt-4">Free to list. No monthly fees.</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-20 grid md:grid-cols-3 gap-8">
        <Bullet
          n="1"
          title="List in 5 minutes"
          body="Snap a few photos, set your price, set a pickup location. Done."
        />
        <Bullet
          n="2"
          title="Renters find you"
          body="When someone nearby needs a trailer like yours, you'll get a booking request."
        />
        <Bullet
          n="3"
          title="We handle payments"
          body="Stripe deposits your earnings straight to your bank in 2–3 days."
        />
      </section>

      <section className="bg-navy-50/40 py-20">
        <div className="mx-auto max-w-5xl px-4 grid md:grid-cols-4 gap-4">
          <Stat label="Average earnings" value="$340/mo" />
          <Stat label="Platform fee" value="10%" />
          <Stat label="Booking-to-payout" value="2–3 days" />
          <Stat label="Damage protection" value="Included" />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h2 className="text-3xl font-bold text-navy mb-3">Ready to start earning?</h2>
        <p className="text-navy-600 mb-6">
          Listing takes about 5 minutes. You can pause or remove your trailer any time.
        </p>
        <Link
          to={signedIn ? '/listings/new' : '/login?next=/listings/new'}
          className="btn-primary text-base px-8"
        >
          List your trailer
        </Link>
      </section>
    </div>
  );
}

function Bullet({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-full bg-orange text-white font-bold flex items-center justify-center mb-3">
        {n}
      </div>
      <h3 className="text-lg font-semibold text-navy mb-1">{title}</h3>
      <p className="text-navy-600 text-sm">{body}</p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-5">
      <div className="text-2xl font-bold text-navy">{value}</div>
      <div className="text-sm text-navy-500 mt-1">{label}</div>
    </div>
  );
}
